import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../shared/utils/api';

const API = import.meta.env.VITE_API_URL || '';

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  // Password change state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const pwCollapseTimer = useRef(null);

  // Delete account state
  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const [delConfirm, setDelConfirm] = useState('');
  const [delError, setDelError] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delDone, setDelDone] = useState(false);

  useEffect(() => {
    api('/api/me')
      .then((data) => { setUser(data); setLoading(false); })
      .catch(() => { setLoading(false); });
    return () => {
      if (pwCollapseTimer.current) clearTimeout(pwCollapseTimer.current);
    };
  }, []);

  const openPortal = () => {
    setPortalLoading(true);
    setPortalError(null);
    api('/api/create-portal-session', { method: 'POST' })
      .then((data) => {
        setPortalLoading(false);
        window.open(data.url, '_blank');
      })
      .catch(() => {
        setPortalLoading(false);
        setPortalError('Could not open billing portal. Please try again or contact support.');
      });
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);

    if (pwNew.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('Passwords do not match.');
      return;
    }

    setPwBusy(true);
    try {
      // Bypass the shared api() helper because it auto-redirects on 401,
      // and the change-password endpoint returns 401 for a wrong current password.
      const token = localStorage.getItem('sqetrack_token') || '';
      const res = await fetch(API + '/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: pwCurrent,
          new_password: pwNew,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setPwCurrent('');
        setPwNew('');
        setPwConfirm('');
        setPwSuccess(true);
        pwCollapseTimer.current = setTimeout(() => {
          setPwOpen(false);
          setPwSuccess(false);
        }, 2000);
      } else if (res.status === 401) {
        setPwError('Current password is incorrect.');
      } else {
        setPwError(body.error || 'Could not update password. Please try again.');
      }
    } catch (_) {
      setPwError('Network error. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    setDelError('');
    setDelBusy(true);
    try {
      const data = await api('/api/account', {
        method: 'DELETE',
        body: JSON.stringify({ confirm: 'DELETE', password: delPassword }),
      });
      if (data && data.deleted) {
        setDelDone(true);
        // Clear the token immediately and redirect after a brief moment so
        // the user sees the confirmation state before the page changes.
        localStorage.removeItem('sqetrack_token');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1800);
      } else {
        setDelError((data && data.error) || 'Could not delete account. Please try again.');
        setDelBusy(false);
      }
    } catch (_) {
      setDelError('Network error. Please try again.');
      setDelBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[640px] mx-auto px-sp-10 py-sp-6">
        <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Profile</h1>
        <p className="text-sm text-text-muted">Loading...</p>
      </div>
    );
  }

  const fields = [
    { label: 'Display name', value: user?.name || '—' },
    { label: 'Email', value: user?.email || '—' },
    {
      label: 'Drills access',
      value: user?.has_drills_access ? 'Active' : 'Not active',
      valueCls: user?.has_drills_access ? 'text-green' : 'text-text-muted',
    },
  ];

  const showPortal = user?.has_drills_access && user?.stripe_customer_id;

  const inputCls =
    'w-full px-sp-3 py-sp-3 text-sm bg-bg-card border border-border-soft rounded-r-md text-text-primary placeholder-text-muted focus:outline-none focus:border-accent transition-colors';

  const labelCls = 'block text-xs font-medium text-text-secondary mb-sp-2';

  const canDelete = delPassword.length > 0 && delConfirm === 'DELETE' && !delBusy && !delDone;

  return (
    <div className="max-w-[640px] mx-auto px-sp-10 py-sp-6">
      <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Profile</h1>

      <div className="bg-bg-card rounded-r-xl border border-border-soft mb-sp-5">
        {fields.map((f, i) => (
          <div
            key={f.label}
            className={`flex items-center justify-between px-sp-6 py-sp-4 ${
              i < fields.length - 1 ? 'border-b border-border-soft' : ''
            }`}
          >
            <span className="text-sm font-medium text-text-secondary">{f.label}</span>
            <span className={`text-sm font-medium ${f.valueCls || 'text-text-primary'}`}>
              {f.value}
            </span>
          </div>
        ))}
      </div>

      {showPortal && (
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-6 py-sp-4 mb-sp-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Billing and invoices</span>
            <button
              type="button"
              onClick={openPortal}
              disabled={portalLoading}
              className="text-sm font-medium text-accent hover:text-accent-hover transition-colors disabled:opacity-50"
            >
              {portalLoading ? 'Opening...' : 'Manage account'}
            </button>
          </div>
          {portalError && (
            <p className="text-xs text-sig-red mt-sp-2">{portalError}</p>
          )}
        </div>
      )}

      {/* Change password */}
      <div className="bg-bg-card rounded-r-xl border border-border-soft mb-sp-4 overflow-hidden">
        <button
          type="button"
          onClick={() => {
            setPwError('');
            setPwSuccess(false);
            setPwOpen((v) => !v);
          }}
          className="w-full flex items-center justify-between px-sp-6 py-sp-4 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
        >
          <span>Change password</span>
          <span className="text-text-muted">{pwOpen ? '−' : '+'}</span>
        </button>

        {pwOpen && (
          <form onSubmit={submitPasswordChange} className="px-sp-6 pb-sp-5 pt-sp-2 border-t border-border-soft">
            <div className="mb-sp-3">
              <label className={labelCls} htmlFor="pw-current">Current password</label>
              <input
                id="pw-current"
                type="password"
                value={pwCurrent}
                onChange={(e) => { setPwCurrent(e.target.value); setPwError(''); }}
                className={inputCls}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="mb-sp-3">
              <label className={labelCls} htmlFor="pw-new">New password</label>
              <input
                id="pw-new"
                type="password"
                value={pwNew}
                onChange={(e) => { setPwNew(e.target.value); setPwError(''); }}
                className={inputCls}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="mb-sp-4">
              <label className={labelCls} htmlFor="pw-confirm">Confirm new password</label>
              <input
                id="pw-confirm"
                type="password"
                value={pwConfirm}
                onChange={(e) => { setPwConfirm(e.target.value); setPwError(''); }}
                className={inputCls}
                autoComplete="new-password"
                required
              />
            </div>

            {pwError && (
              <p className="text-xs text-sig-red mb-sp-3">{pwError}</p>
            )}
            {pwSuccess && (
              <p className="text-xs text-green mb-sp-3">Password updated.</p>
            )}

            <button
              type="submit"
              disabled={pwBusy}
              className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {pwBusy ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}
      </div>

      {/* Delete account */}
      <div className="bg-sig-red-bg border border-sig-red rounded-r-xl px-sp-6 py-sp-5 mb-sp-4">
        <h2 className="text-sm font-semibold text-sig-red-text mb-sp-2">Delete account</h2>
        <p className="text-xs text-text-secondary leading-relaxed mb-sp-4">
          Permanently delete your SQETrack account and your drill history. This cannot be undone.
          Your Stripe payment record will be retained for accounting purposes, you can still
          access historical invoices from Stripe directly via the &lsquo;Manage account&rsquo; button above.
        </p>

        {!delOpen && !delDone && (
          <button
            type="button"
            onClick={() => { setDelOpen(true); setDelError(''); }}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-sig-red text-white rounded-r-md hover:opacity-90 transition-opacity"
          >
            Delete my account
          </button>
        )}

        {delOpen && !delDone && (
          <form onSubmit={submitDelete}>
            <div className="mb-sp-3">
              <label className={labelCls} htmlFor="del-password">Current password</label>
              <input
                id="del-password"
                type="password"
                value={delPassword}
                onChange={(e) => { setDelPassword(e.target.value); setDelError(''); }}
                className={inputCls}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="mb-sp-4">
              <label className={labelCls} htmlFor="del-confirm">
                Type DELETE to confirm
              </label>
              <input
                id="del-confirm"
                type="text"
                value={delConfirm}
                onChange={(e) => { setDelConfirm(e.target.value); setDelError(''); }}
                className={inputCls}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
              />
            </div>

            {delError && (
              <p className="text-xs text-sig-red mb-sp-3">{delError}</p>
            )}

            <div className="flex items-center gap-sp-3">
              <button
                type="submit"
                disabled={!canDelete}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-sig-red text-white rounded-r-md hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {delBusy ? 'Deleting...' : 'Permanently delete'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDelOpen(false);
                  setDelPassword('');
                  setDelConfirm('');
                  setDelError('');
                }}
                disabled={delBusy}
                className="text-sm font-medium text-text-muted hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {delDone && (
          <p className="text-sm font-medium text-sig-red-text">
            Your account has been deleted. Redirecting...
          </p>
        )}
      </div>
    </div>
  );
}
